"use client"

import { useEffect, useRef, useState } from "react"
import api from "../utils/api"
import Icon from "../components/Icon"
import { maskKraPin } from "../utils/kraNameMatch"
import { useNavigate } from "react-router-dom"
import { initializeAdminSessionForToken } from "../utils/adminSession"

// ── Small helpers ─────────────────────────────────────────────────────────────
// Styled input wrapper with a left icon
function IconInput({ iconName, children }) {
  return (
    <div className="relative flex items-center">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600 pointer-events-none" style={{ fontSize: "1.05rem" }}>
        <Icon name={iconName} />
      </span>
      {children}
    </div>
  )
}

function LoginPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ idNumber: "", pin: "" })
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [isRegister, setIsRegister] = useState(false)
  const [showForgot, setShowForgot] = useState(false)
  const [forgotStep, setForgotStep] = useState("request") // "request" | "verify"
  const [forgotLoading, setForgotLoading] = useState(false)
  const [forgotMessage, setForgotMessage] = useState("")
  const [forgotError, setForgotError] = useState("")
  const [forgotMethod, setForgotMethod] = useState("phone") // "email" | "phone"
  const [forgotForm, setForgotForm] = useState({
    idNumber: "",
    email: "",
    phone: "",
    code: "",
    newPin: "",
    confirmNewPin: ""
  })
  const [registerForm, setRegisterForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    idNumber: "",
    confirmIdNumber: "",
    pin: "",
    confirmPin: ""
  })
  const [registerErrors, setRegisterErrors] = useState({})
  const [registerVerificationModal, setRegisterVerificationModal] = useState({
    open: false,
    canProceed: false,
    issues: [],
    duplicateIdNumber: false,
    duplicateEmail: false,
    existingUser: null,
    kraStatus: null,
  })
  const registerRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    idNumber: useRef(null),
    confirmIdNumber: useRef(null),
    pin: useRef(null),
    confirmPin: useRef(null)
  }


  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
    if (error) setError("")
  }

  const getApiError = (err, fallbackMessage) => {
    const payload = err?.response?.data
    if (typeof payload?.error === "string" && payload.error.trim()) return payload.error
    if (typeof payload?.message === "string" && payload.message.trim()) return payload.message
    if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
      const first = payload.errors.find((item) => typeof item === "string" && item.trim())
      if (first) return first
    }
    if (typeof err?.message === "string" && err.message.trim()) return err.message
    return fallbackMessage
  }

  const mapRegisterErrorMessage = (message) => {
    const text = String(message || "").trim()
    if (!text) return "Registration failed"
    const lower = text.toLowerCase()
    // Registration UI intentionally has no email field; show the actionable field.
    if (lower.includes("email already registered")) return "ID Number already registered"
    return text
  }

  const closeRegisterVerificationModal = () => {
    setRegisterVerificationModal({
      open: false,
      canProceed: false,
      issues: [],
      duplicateIdNumber: false,
      duplicateEmail: false,
      existingUser: null,
      kraStatus: null,
    })
  }

  const handleForgotChange = (e) => {
    setForgotForm({ ...forgotForm, [e.target.name]: e.target.value })
    if (forgotError) setForgotError("")
    if (forgotMessage) setForgotMessage("")
  }

  const setForgotDeliveryMethod = (method) => {
    setForgotMethod(method)
    // Clear the non-selected field to avoid confusion
    setForgotForm(prev => ({
      ...prev,
      email: method === "email" ? prev.email : "",
      phone: method === "phone" ? prev.phone : ""
    }))
    if (forgotError) setForgotError("")
    if (forgotMessage) setForgotMessage("")
  }

  // Temporarily disable email-based reset in the UI (SMTP not configured).
  // Backend logic remains intact for future re-enabling.
  useEffect(() => {
    if (forgotMethod !== "phone") {
      setForgotMethod("phone")
      setForgotForm(prev => ({ ...prev, email: "" }))
    }
  }, [forgotMethod])

  const handleRegisterChange = (e) => {
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value })
    if (error) setError("")
    if (registerErrors[e.target.name]) {
      setRegisterErrors(prev => ({ ...prev, [e.target.name]: "" }))
    }
  }

  // (Banks catalog removed from registration UI; backend logic remains unchanged.)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await api.post("/auth/login", form)
      const token = res?.data?.token
      if (!token) throw new Error("Missing auth token")
      localStorage.setItem("token", token)
      initializeAdminSessionForToken(token)
      const role = res?.data?.user?.role
      if (role === 'A') navigate('/admin')
      else navigate("/dashboard")
    } catch (err) {
      setError(getApiError(err, "Login failed"))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotRequestSubmit = async (e) => {
    e.preventDefault()
    setForgotError("")
    setForgotMessage("")

    const idNumber = forgotForm.idNumber?.trim()
    const email = forgotForm.email?.trim()
    const phone = forgotForm.phone?.trim()

    if (!idNumber) {
      setForgotError("ID Number is required")
      return
    }

    if (forgotMethod === "email") {
      if (!email) {
        setForgotError("ID Number and email are required")
        return
      }
    } else if (forgotMethod === "phone") {
      if (!phone) {
        setForgotError("ID Number and phone are required")
        return
      }
    } else if (!email && !phone) {
      setForgotError("Please provide either the email or phone you registered with")
      return
    }

    setForgotLoading(true)
    try {
      const payload = {
        idNumber,
        method: forgotMethod
      }
      if (forgotMethod === "email") {
        payload.email = email
      } else if (forgotMethod === "phone") {
        payload.phone = phone
      }

      const res = await api.post("/auth/forgot-pin/request", payload)
      setForgotMessage(res.data?.message || "If an account exists, a reset code has been sent.")
      setForgotStep("verify")
    } catch (err) {
      setForgotError(err.response?.data?.error || "Failed to start reset flow")
    } finally {
      setForgotLoading(false)
    }
  }

  const handleForgotVerifySubmit = async (e) => {
    e.preventDefault()
    setForgotError("")
    setForgotMessage("")

    if (!forgotForm.idNumber || !forgotForm.code || !forgotForm.newPin || !forgotForm.confirmNewPin) {
      setForgotError("All fields are required")
      return
    }

    if (forgotForm.newPin !== forgotForm.confirmNewPin) {
      setForgotError("PINs do not match")
      return
    }

    if (!/^\d{4}$/.test(forgotForm.newPin)) {
      setForgotError("New PIN must be exactly 4 digits")
      return
    }

    setForgotLoading(true)
    try {
      const res = await api.post("/auth/forgot-pin/verify", {
        idNumber: forgotForm.idNumber,
        code: forgotForm.code,
        newPin: forgotForm.newPin
      })
      setForgotMessage(res.data?.message || "PIN reset successfully. Please log in with your new PIN.")

      // Pre-fill login form ID and close forgot UI after a moment
      setForm(prev => ({ ...prev, idNumber: forgotForm.idNumber, pin: "" }))
      setTimeout(() => {
        setShowForgot(false)
        setForgotStep("request")
        setForgotForm({
          idNumber: "",
          email: "",
          phone: "",
          code: "",
          newPin: "",
          confirmNewPin: ""
        })
        setForgotError("")
      }, 1000)
    } catch (err) {
      setForgotError(err.response?.data?.error || "Failed to reset PIN")
    } finally {
      setForgotLoading(false)
    }
  }

  const buildRegisterSubmitPayload = () => {
    const { confirmPin, confirmIdNumber, firstName, lastName, ...submitData } = registerForm
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim()

    const rawId = String(submitData.idNumber || "").trim()
    const safeId = rawId.replace(/[^a-zA-Z0-9]/g, "") || `user${Date.now()}`
    const uniqueSuffix = `${Date.now()}${Math.floor(Math.random() * 100000)}`
    const generatedEmail = `noemail+${safeId}.${uniqueSuffix}@recordlogs.invalid`

    return {
      ...submitData,
      name: fullName,
      email: generatedEmail,
      bankName: "",
      bankAccNumber: "",
      paybill: ""
    }
  }

  const submitRegistration = async () => {
    const submitPayload = buildRegisterSubmitPayload()
    await api.post("/auth/register", submitPayload)
    setError("")

    try {
      const loginRes = await api.post("/auth/login", {
        idNumber: submitPayload.idNumber,
        pin: submitPayload.pin
      })
      const token = loginRes?.data?.token
      if (!token) throw new Error("Missing auth token")
      localStorage.setItem("token", token)
      initializeAdminSessionForToken(token)
      const role = loginRes?.data?.user?.role
      if (role === 'A') navigate('/admin')
      else navigate("/dashboard")
    } catch (loginErr) {
      alert("Registration successful! Please login with your credentials.")
      setIsRegister(false)
      setForm({ idNumber: submitPayload.idNumber, pin: "" })
    }

    setRegisterForm({
      firstName: "",
      lastName: "",
      phone: "",
      idNumber: "",
      confirmIdNumber: "",
      pin: "",
      confirmPin: ""
    })
  }

  const handleRegisterSubmit = async (e) => {
    e.preventDefault()

    const newErrors = {}
    if (!registerForm.firstName.trim()) {
      newErrors.firstName = "First name is required"
    }
    if (!registerForm.lastName.trim()) {
      newErrors.lastName = "Last name is required"
    }
    if (registerForm.idNumber !== registerForm.confirmIdNumber) {
      newErrors.confirmIdNumber = "ID Numbers do not match"
    }
    if (registerForm.pin !== registerForm.confirmPin) {
      newErrors.confirmPin = "PINs do not match"
    }
    if (!/^\d{4}$/.test(registerForm.pin)) {
      newErrors.pin = "PIN must be exactly 4 digits"
    }
    if (!termsAgreed) {
      newErrors.terms = "You must agree to the Terms, Rules & Regulations to continue"
    }
    if (Object.keys(newErrors).length > 0) {
      setRegisterErrors(newErrors)
      setError("")
      const order = ["firstName", "lastName", "confirmIdNumber", "pin", "confirmPin", "idNumber"]
      const firstKey = order.find(k => newErrors[k])
      if (firstKey && registerRefs[firstKey]?.current) {
        try {
          registerRefs[firstKey].current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        } catch {}
        try {
          registerRefs[firstKey].current.focus()
        } catch {}
      }
      return
    }

    setRegisterErrors({})
    setLoading(true)
    try {
      const previewRes = await api.post("/auth/register/preview", buildRegisterSubmitPayload())
      setRegisterVerificationModal({
        open: true,
        canProceed: !!previewRes?.data?.canProceed,
        issues: Array.isArray(previewRes?.data?.issues) ? previewRes.data.issues : [],
        duplicateIdNumber: !!previewRes?.data?.duplicateIdNumber,
        duplicateEmail: !!previewRes?.data?.duplicateEmail,
        existingUser: previewRes?.data?.existingUser || null,
        kraStatus: previewRes?.data?.kraStatus || null,
      })
    } catch (err) {
      setRegisterErrors({})
      setError(mapRegisterErrorMessage(getApiError(err, "Registration failed")))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmRegistration = async () => {
    setLoading(true)
    try {
      await submitRegistration()
      closeRegisterVerificationModal()
    } catch (err) {
      setRegisterErrors({})
      setError(mapRegisterErrorMessage(getApiError(err, "Registration failed")))
      closeRegisterVerificationModal()
    } finally {
      setLoading(false)
    }
  }

  const [showPin, setShowPin] = useState(false)
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)

  return (
    <div className="min-h-screen font-sans relative flex flex-col" style={{ background: "linear-gradient(135deg, #f0faf4 0%, #e8f5ee 50%, #f5fdf7 100%)" }}>
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('/images/bgimage.jpg')", opacity: 0.08 }}
      />

      {/* ── Navbar ──────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "#ffffff",
        borderBottom: "1.5px solid #e5e7eb",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)"
      }}>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 1.25rem", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Brand */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div style={{
              width: 40, height: 40,
              background: "#1a5c36",
              borderRadius: 10,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              overflow: "hidden"
            }}>
              <img src="/logofinalrounded.png" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} onError={e => { e.target.style.display='none'; e.target.parentNode.innerHTML='<span style="color:#fff;font-weight:700;font-size:1.1rem;">R</span>'; }} />
            </div>
            <span style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1a5c36", letterSpacing: "-0.01em" }}>RecordLOGS</span>
          </div>
          {/* Home button */}
          <a href="/" style={{
            display: "inline-flex", alignItems: "center", gap: "0.45rem",
            padding: "0.45rem 1.1rem",
            border: "1.5px solid #d1d5db",
            borderRadius: 10,
            background: "#fff",
            color: "#374151",
            fontWeight: 500,
            fontSize: "0.9rem",
            textDecoration: "none",
            transition: "border-color 0.15s, background 0.15s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.07)"
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor="#10b981"; e.currentTarget.style.background="#f0fdf4"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor="#d1d5db"; e.currentTarget.style.background="#fff"; }}
          >
            <Icon name="house" size="0.95rem" />
            <span>Home</span>
          </a>
        </div>
      </nav>

      {/* ── Page body ──────────────────────────────────────────────────────── */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-4">
        {/* Brand heading */}
        <div className="text-center mb-3">
          <h1 style={{ fontSize: "1.6rem", fontWeight: 800, color: "#1a5c36", letterSpacing: "-0.02em", marginBottom: "0.15rem" }}>
            RecordLOGS
          </h1>
          <p style={{ color: "#6b7280", fontSize: "0.82rem" }}>All-In-One Records Platform</p>
        </div>

        <div style={{ width: "100%", maxWidth: 460 }}>

          {/* Card */}
          <div style={{
            background: "#fff",
            borderRadius: 18,
            padding: "1.4rem 1.5rem 1.25rem",
            boxShadow: "0 4px 32px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
            border: "1px solid #f3f4f6"
          }}>
            {/* Card top icon */}
            <div className="flex flex-col items-center mb-3" style={{ textAlign: "center" }}>
              <div style={{
                width: 46, height: 46,
                borderRadius: "50%",
                background: "#d1fae5",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: "0.6rem",
                boxShadow: "0 2px 8px rgba(16,185,129,0.15)"
              }}>
                <Icon name={isRegister ? "person-plus" : "journal-richtext"} size="1.2rem" style={{ color: "#059669" }} />
              </div>
              <h2 style={{ fontWeight: 700, fontSize: "1.2rem", color: "#111827", marginBottom: "0.15rem" }}>
                {isRegister ? "Create Account" : "Welcome Back"}
              </h2>
              <p style={{ color: "#6b7280", fontSize: "0.8rem", textAlign: "center" }}>
                {isRegister ? "Register for access to the system" : "Sign in to access your records and modules"}
              </p>
            </div>

          {error && !showForgot && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          {forgotError && showForgot && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{forgotError}</p>
            </div>
          )}
          {forgotMessage && showForgot && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <p className="text-emerald-700 text-sm">{forgotMessage}</p>
            </div>
          )}

          {!isRegister && !showForgot ? (
            // Login Form
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#374151" }}>ID Number</label>
                <IconInput iconName="person">
                  <input
                    type="text"
                    name="idNumber"
                    id="login-idNumber"
                    placeholder="Enter your ID Number"
                    value={form.idNumber}
                    onChange={handleChange}
                    required
                    style={{ paddingLeft: "2.5rem" }}
                    className="w-full h-11 pr-3 border border-gray-200 rounded-xl text-sm transition-all outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/20"
                  />
                </IconInput>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "#374151" }}>PIN / Password</label>
                <IconInput iconName="lock">
                  <input
                    type={showPin ? "text" : "password"}
                    name="pin"
                    id="login-pin"
                    placeholder="Enter your PIN or password"
                    value={form.pin}
                    onChange={handleChange}
                    required
                    style={{ paddingLeft: "2.5rem", paddingRight: "2.75rem" }}
                    className="w-full h-11 border border-gray-200 rounded-xl text-sm transition-all outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-400/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-emerald-600 transition-colors"
                    tabIndex={-1}
                  >
                    <Icon name={showPin ? "eye-slash" : "eye"} size="1rem" />
                  </button>
                </IconInput>
              </div>

              <button
                type="submit"
                id="login-submit"
                disabled={loading}
                className="w-full h-11 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 text-white mt-1"
                style={{
                  background: loading ? "#9ca3af" : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: loading ? "none" : "0 3px 12px rgba(16,185,129,0.35)"
                }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Signing In...
                  </>
                ) : (
                  <>
                    <Icon name="lock" size="1rem" />
                    Sign In
                  </>
                )}
              </button>

              {/* Link rows */}
              <div className="mt-1 space-y-1 pt-1">
                <button
                  type="button"
                  id="forgot-pin-btn"
                  onClick={() => {
                    setShowForgot(true)
                    setForgotStep("request")
                    setForgotError("")
                    setForgotMessage("")
                  }}
                  className="w-full flex items-center justify-center gap-2 py-1.5 text-sm font-medium transition-colors"
                  style={{ color: "#059669" }}
                >
                  <Icon name="key" size="1rem" />
                  Forgot PIN or password?
                </button>
              </div>
            </form>
          ) : null}

          {!isRegister && showForgot ? (
            // Forgot PIN / Password flow
            <div className="space-y-4">
              {forgotStep === "request" ? (
                <form onSubmit={handleForgotRequestSubmit} className="space-y-4">
                  <div className="text-left text-sm text-gray-600">
                    Use the details you registered with. Reset codes are sent via SMS (email reset is temporarily disabled).
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">ID Number</label>
                    <input
                      type="text"
                      name="idNumber"
                      placeholder="Your ID Number"
                      value={forgotForm.idNumber}
                      onChange={handleForgotChange}
                      required
                      className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    SMS delivery fee: KSh 5.
                  </div>

                  {forgotMethod === "email" ? (
                    <div className="transition-all">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email on your account"
                        value={forgotForm.email}
                        onChange={handleForgotChange}
                        required
                        className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                      />
                    </div>
                  ) : (
                    <div className="transition-all">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone on your account"
                        value={forgotForm.phone}
                        onChange={handleForgotChange}
                        required
                        className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                      />
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className={`w-full h-11 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                      forgotLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 hover:-translate-y-0.5 hover:shadow-lg text-white shadow-md"
                    }`}
                  >
                    {forgotLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Sending code...
                      </>
                    ) : (
                      "Send reset code"
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleForgotVerifySubmit} className="space-y-4">
                  <div className="text-left text-sm text-gray-600">
                    Enter the code sent to you and choose a new 4-digit PIN.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">ID Number</label>
                    <input
                      type="text"
                      name="idNumber"
                      placeholder="Your ID Number"
                      value={forgotForm.idNumber}
                      onChange={handleForgotChange}
                      required
                      className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Reset Code</label>
                    <input
                      type="text"
                      name="code"
                      placeholder="6-digit code"
                      value={forgotForm.code}
                      onChange={handleForgotChange}
                      required
                      className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">New 4-Digit PIN</label>
                      <input
                        type="password"
                        name="newPin"
                        placeholder="New PIN"
                        value={forgotForm.newPin}
                        onChange={handleForgotChange}
                        maxLength="4"
                        pattern="[0-9]{4}"
                        required
                        className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm New PIN</label>
                      <input
                        type="password"
                        name="confirmNewPin"
                        placeholder="Confirm new PIN"
                        value={forgotForm.confirmNewPin}
                        onChange={handleForgotChange}
                        maxLength="4"
                        pattern="[0-9]{4}"
                        required
                        className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className={`w-full h-11 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                      forgotLoading
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 hover:-translate-y-0.5 hover:shadow-lg text-white shadow-md"
                    }`}
                  >
                    {forgotLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Updating PIN...
                      </>
                    ) : (
                      "Reset PIN"
                    )}
                  </button>
                </form>
              )}

              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgot(false)
                    setForgotStep("request")
                    setForgotForm({
                      idNumber: "",
                      email: "",
                      phone: "",
                      code: "",
                      newPin: "",
                      confirmNewPin: ""
                    })
                    setForgotMethod("phone")
                    setForgotError("")
                    setForgotMessage("")
                  }}
                  className="text-sm text-gray-600 hover:text-gray-700 underline font-medium"
                >
                  Back to sign in
                </button>
              </div>
            </div>
          ) : null}

          {isRegister && (
            // Registration Form
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Your first name"
                    value={registerForm.firstName}
                    onChange={handleRegisterChange}
                    required
                    ref={registerRefs.firstName}
                    aria-invalid={Boolean(registerErrors.firstName) || undefined}
                    aria-describedby={registerErrors.firstName ? "firstName-error" : undefined}
                    className={`w-full h-11 px-3 border-2 rounded-lg text-base transition-all outline-none ${
                      registerErrors.firstName
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-400/20"
                        : "border-gray-200 focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    }`}
                  />
                  {registerErrors.firstName && (
                    <p id="firstName-error" className="mt-1 text-xs text-red-600">{registerErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Your last name"
                    value={registerForm.lastName}
                    onChange={handleRegisterChange}
                    required
                    ref={registerRefs.lastName}
                    aria-invalid={Boolean(registerErrors.lastName) || undefined}
                    aria-describedby={registerErrors.lastName ? "lastName-error" : undefined}
                    className={`w-full h-11 px-3 border-2 rounded-lg text-base transition-all outline-none ${
                      registerErrors.lastName
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-400/20"
                        : "border-gray-200 focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    }`}
                  />
                  {registerErrors.lastName && (
                    <p id="lastName-error" className="mt-1 text-xs text-red-600">{registerErrors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your phone number"
                  value={registerForm.phone}
                  onChange={handleRegisterChange}
                  required
                  className="w-full h-11 px-3 border-2 border-gray-200 rounded-lg text-base transition-all outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">ID Number</label>
                  <input
                    type="text"
                    name="idNumber"
                    placeholder="Your national ID number"
                    value={registerForm.idNumber}
                    onChange={handleRegisterChange}
                    required
                    ref={registerRefs.idNumber}
                    aria-invalid={Boolean(registerErrors.idNumber) || undefined}
                    className={`w-full h-11 px-3 border-2 rounded-lg text-base transition-all outline-none ${
                      registerErrors.idNumber
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-400/20"
                        : "border-gray-200 focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm ID Number</label>
                  <input
                    type="text"
                    name="confirmIdNumber"
                    placeholder="Confirm your national ID number"
                    value={registerForm.confirmIdNumber}
                    onChange={handleRegisterChange}
                    required
                    ref={registerRefs.confirmIdNumber}
                    aria-invalid={Boolean(registerErrors.confirmIdNumber) || undefined}
                    aria-describedby={registerErrors.confirmIdNumber ? "confirmIdNumber-error" : undefined}
                    className={`w-full h-11 px-3 border-2 rounded-lg text-base transition-all outline-none ${
                      registerErrors.confirmIdNumber
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-400/20"
                        : "border-gray-200 focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    }`}
                  />
                  {registerErrors.confirmIdNumber && (
                    <p id="confirmIdNumber-error" className="mt-1 text-xs text-red-600">
                      {registerErrors.confirmIdNumber}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">4-Digit PIN</label>
                  <input
                    type="password"
                    name="pin"
                    placeholder="Create a 4-digit PIN"
                    value={registerForm.pin}
                    onChange={handleRegisterChange}
                    maxLength="4"
                    pattern="[0-9]{4}"
                    required
                    ref={registerRefs.pin}
                    aria-invalid={Boolean(registerErrors.pin) || undefined}
                    aria-describedby={registerErrors.pin ? "pin-error" : undefined}
                    className={`w-full h-11 px-3 border-2 rounded-lg text-base transition-all outline-none ${
                      registerErrors.pin
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-400/20"
                        : "border-gray-200 focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    }`}
                  />
                  {registerErrors.pin && (
                    <p id="pin-error" className="mt-1 text-xs text-red-600">{registerErrors.pin}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm PIN</label>
                  <input
                    type="password"
                    name="confirmPin"
                    placeholder="Confirm your PIN"
                    value={registerForm.confirmPin}
                    onChange={handleRegisterChange}
                    maxLength="4"
                    pattern="[0-9]{4}"
                    required
                    ref={registerRefs.confirmPin}
                    aria-invalid={Boolean(registerErrors.confirmPin) || undefined}
                    aria-describedby={registerErrors.confirmPin ? "confirmPin-error" : undefined}
                    className={`w-full h-11 px-3 border-2 rounded-lg text-base transition-all outline-none ${
                      registerErrors.confirmPin
                        ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-400/20"
                        : "border-gray-200 focus:border-green-400 focus:ring-2 focus:ring-green-400/20"
                    }`}
                  />
                  {registerErrors.confirmPin && (
                    <p id="confirmPin-error" className="mt-1 text-xs text-red-600">{registerErrors.confirmPin}</p>
                  )}
                </div>
              </div>

              {/* Terms & Regulations */}
              <div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.625rem",
                    cursor: "pointer",
                    padding: "0.625rem 0.75rem",
                    borderRadius: 10,
                    border: registerErrors.terms
                      ? "1.5px solid #fca5a5"
                      : termsAgreed
                      ? "1.5px solid #6ee7b7"
                      : "1.5px solid #e5e7eb",
                    background: registerErrors.terms
                      ? "#fff5f5"
                      : termsAgreed
                      ? "#f0fdf4"
                      : "#f9fafb",
                    transition: "border-color 0.15s, background 0.15s"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={termsAgreed}
                    onChange={e => {
                      setTermsAgreed(e.target.checked)
                      if (e.target.checked && registerErrors.terms) {
                        setRegisterErrors(prev => ({ ...prev, terms: "" }))
                      }
                    }}
                    style={{
                      marginTop: "0.15rem",
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      accentColor: "#059669",
                      cursor: "pointer"
                    }}
                  />
                  <span style={{ fontSize: "0.76rem", color: registerErrors.terms ? "#b91c1c" : "#374151", lineHeight: 1.55 }}>
                    I HAVE READ AND AGREE TO THE RECORDLOGS (RL){" "}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      style={{
                        color: "#059669",
                        fontWeight: 600,
                        textDecoration: "underline",
                        textUnderlineOffset: 2,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontSize: "inherit"
                      }}
                    >
                      Terms, Rules &amp; Regulations
                    </button>
                    {" "}EFFECTIVE JANUARY 1, 2026.
                  </span>
                </label>
                {registerErrors.terms && (
                  <p style={{ marginTop: "0.25rem", fontSize: "0.72rem", color: "#dc2626", display: "flex", alignItems: "center", gap: "0.3rem" }}>
                    <Icon name="exclamation-circle" size="0.75rem" />
                    {registerErrors.terms}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full h-11 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 hover:-translate-y-0.5 hover:shadow-lg text-white shadow-md"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </button>
            </form>
          )}

          {registerVerificationModal.open && (
            <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-2 py-4 sm:px-4 sm:py-6">
              <div className="w-full max-w-lg rounded-[24px] sm:rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-4 sm:px-5 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white">
                  <h3 className="text-lg font-semibold text-gray-900">Review Verification</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Check the verification results before creating the account.
                  </p>
                </div>

                <div className="px-4 py-4 sm:px-5 space-y-4">
                  {registerVerificationModal.issues.length > 0 ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <div className="text-sm font-semibold text-amber-800">Verification issues found</div>
                      <div className="mt-2 space-y-1">
                        {registerVerificationModal.issues.map((issue, index) => (
                          <p key={`${issue}-${index}`} className="text-sm text-amber-700">
                            {issue}
                          </p>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                      All verification checks passed. You can proceed with registration.
                    </div>
                  )}

                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-sm font-semibold text-slate-800">ID verification</div>
                    <div className="mt-2 space-y-1 break-words text-sm text-slate-700">
                      <p>Status: {registerVerificationModal.kraStatus?.kraNameMatches ? "Verified" : "Not verified"}</p>
                      <p>PIN: {registerVerificationModal.kraStatus?.kraPin ? maskKraPin(registerVerificationModal.kraStatus.kraPin) : "Not available"}</p>
                      {registerVerificationModal.kraStatus?.kraNameMatches ? (
                        <p>ID Name: {registerVerificationModal.kraStatus?.kraTaxpayerName || "Not available"}</p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-4 py-4 sm:px-5 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeRegisterVerificationModal}
                    disabled={loading}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmRegistration}
                    disabled={loading || !registerVerificationModal.canProceed}
                    className={`w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition sm:w-auto ${
                      loading || !registerVerificationModal.canProceed
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-700"
                    }`}
                  >
                    {loading ? "Creating Account..." : registerVerificationModal.canProceed ? "Proceed Registration" : "Cannot Proceed"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Terms Modal */}
          {showTermsModal && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6"
              style={{ background: "rgba(0,0,0,0.55)" }}
              onClick={e => { if (e.target === e.currentTarget) setShowTermsModal(false) }}
            >
              <div style={{
                width: "100%",
                maxWidth: 520,
                background: "#fff",
                borderRadius: 18,
                boxShadow: "0 8px 48px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                maxHeight: "85vh",
                overflow: "hidden"
              }}>
                {/* Modal header */}
                <div style={{
                  padding: "1rem 1.25rem",
                  borderBottom: "1px solid #e5e7eb",
                  background: "linear-gradient(135deg, #f0fdf4 0%, #fff 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexShrink: 0
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Icon name="file-earmark-text" size="1rem" style={{ color: "#059669" }} />
                    <div>
                      <p style={{ fontWeight: 700, fontSize: "0.95rem", color: "#111827", lineHeight: 1.2 }}>Terms, Rules &amp; Regulations</p>
                      <p style={{ fontSize: "0.72rem", color: "#6b7280", marginTop: "0.1rem" }}>Effective January 1, 2026</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(false)}
                    style={{
                      width: 30, height: 30,
                      borderRadius: "50%",
                      background: "#f3f4f6",
                      border: "none",
                      cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#6b7280",
                      flexShrink: 0
                    }}
                  >
                    <Icon name="x-lg" size="0.8rem" />
                  </button>
                </div>

                {/* Modal body — scrollable */}
                <div style={{ overflowY: "auto", padding: "1.25rem", fontSize: "0.82rem", color: "#374151", lineHeight: 1.7, flex: 1 }}>
                  <p style={{ marginBottom: "0.75rem" }}>
                    By creating a RecordLOGS (RL) account, registering for any RL module, or using any RL service, you agree to the following:
                  </p>

                  <ol style={{ paddingLeft: "1.25rem", marginBottom: "1rem" }}>
                    <li style={{ marginBottom: "0.5rem" }}>All information provided must be accurate, complete, and truthful.</li>
                    <li style={{ marginBottom: "0.5rem" }}>RL may verify identity, contact information, telephone numbers, and supporting documents at any time.</li>
                    <li style={{ marginBottom: "0.5rem" }}>Users are responsible for maintaining the security of their accounts and login credentials.</li>
                    <li style={{ marginBottom: "0.5rem" }}>RL maintains a PaySCORE that measures payment reliability and account performance across RL modules. PaySCORE may consider on-time payments, late payments, outstanding balances, defaults, returned transactions, and compliance with module requirements.</li>
                    <li style={{ marginBottom: "0.5rem" }}>Authorized users, organizations, management, landlords, administrators, or module operators may view PaySCORE and other information relevant to services provided through RL.</li>
                    <li style={{ marginBottom: "0.5rem" }}>A low PaySCORE may affect eligibility for certain services, approvals, memberships, benefits, unit assignments, credit arrangements, or other platform features.</li>
                    <li style={{ marginBottom: "0.5rem" }}>Users shall not provide false information, commit fraud, misuse the platform, access unauthorized information, or interfere with RL operations.</li>
                    <li style={{ marginBottom: "0.5rem" }}>All transactions recorded by RL are considered official platform records unless proven otherwise.</li>
                    <li style={{ marginBottom: "0.5rem" }}>RL may retain user, transaction, payment, communication, and activity records for operational, security, audit, compliance, and legal purposes.</li>
                    <li style={{ marginBottom: "0.5rem" }}>Access to modules and information is limited to authorized roles and permissions assigned within RL.</li>
                    <li style={{ marginBottom: "0.5rem" }}>RL may suspend, restrict, or terminate accounts for violations of these Terms, fraudulent activity, false information, misuse of services, or legal requirements.</li>
                    <li style={{ marginBottom: "0.5rem" }}>RL may amend these Terms from time to time. Continued use of RL constitutes acceptance of any updated Terms.</li>
                  </ol>

                  <p style={{ marginBottom: "0.75rem" }}>By selecting "I Agree", creating an account, or using RL services, you acknowledge and agree that:</p>
                  <ul style={{ listStyle: "none", padding: 0, marginBottom: "1rem" }}>
                    <li style={{ marginBottom: "0.4rem", display: "flex", gap: "0.5rem" }}><span style={{ color: "#059669", fontWeight: 700 }}>&#10004;</span> The information you provide is accurate and truthful.</li>
                    <li style={{ marginBottom: "0.4rem", display: "flex", gap: "0.5rem" }}><span style={{ color: "#059669", fontWeight: 700 }}>&#10004;</span> You consent to identity and account verification.</li>
                    <li style={{ marginBottom: "0.4rem", display: "flex", gap: "0.5rem" }}><span style={{ color: "#059669", fontWeight: 700 }}>&#10004;</span> You understand and accept the use of RL PaySCORE.</li>
                    <li style={{ marginBottom: "0.4rem", display: "flex", gap: "0.5rem" }}><span style={{ color: "#059669", fontWeight: 700 }}>&#10004;</span> You agree to comply with all RecordLOGS (RL) Terms, Rules &amp; Regulations.</li>
                  </ul>
                </div>

                {/* Modal footer */}
                <div style={{
                  padding: "0.875rem 1.25rem",
                  borderTop: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  flexShrink: 0
                }}>
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(false)}
                    style={{
                      width: "100%",
                      height: 42,
                      borderRadius: 10,
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(16,185,129,0.3)"
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

            {/* Register / Sign-in toggle */}
            <div className="mt-2 text-center">
              <button
                id="toggle-register-btn"
                onClick={() => {
                  setIsRegister(!isRegister)
                  setError("")
                  setRegisterErrors({})
                  setTermsAgreed(false)
                }}
                className="w-full flex items-center justify-center gap-2 py-1.5 text-sm font-medium transition-colors"
                style={{ color: "#059669" }}
              >
                <Icon name={isRegister ? "box-arrow-in-right" : "person-plus"} size="1rem" />
                {isRegister ? "Already have an account? Sign In" : "New account? Register"}
              </button>
            </div>

            {/* Contact */}
            <div className="pb-1 text-center">
              <p className="text-xs flex items-center justify-center gap-1.5" style={{ color: "#9ca3af" }}>
                <Icon name="question-circle" size="0.85rem" />
                Need help?
                <button
                  id="contact-admin-btn"
                  className="font-medium underline transition-colors"
                  style={{ color: "#374151" }}
                >
                  Contact administrator
                </button>
              </p>
            </div>
          </div>{/* end card */}
        </div>{/* end max-width wrapper */}

        <div className="text-center mt-8">
          <p className="text-xs" style={{ color: "#d1d5db" }}>© 2025 RecordLOGS. All rights reserved.</p>
        </div>
      </div>{/* end page body */}
    </div>
  )
}

export default LoginPage
